import { FC, useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import useVehicle from 'hooks/lp/data/vehicle/useVehicle';
import { useLayoutContext } from 'contexts/LayoutContext';
import CurrencyRate from 'components/CurrencyRate';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { CurrencyRateDetails } from 'further-types/lp/transaction';
import { CalendarDay, pluralize } from 'further-ui/utils';
import PayablesOrReceivables from './PayablesOrReceivables';
import Cash from './Cash';
import ProcessingPage from 'components/new/ProcessingPage';
import TabsPanel from 'further-ui/components/TabsPanel';
import { styled } from '@mui/material/styles';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import { useDisclosure, usePagination } from 'further-ui/hooks';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { createLpPeriodEndReviewDetailRoute } from 'adminConstants/routes';
import useEqualisations from 'hooks/lp/data/equalisations/useEqualisations';
import PendingDraftEqualisationsModal from 'components/PendingDraftEqualisationsModal';
import FullPageDrawer from 'components/new/FullPageDrawer';
import { Column, Row } from 'further-ui/components/Layout';
import PayablesOrReceivablesHistoryDetail from './PayablesOrReceivablesHistoryDetail';
import CashHistoryDetailEmbedded from './CashHistoryDetailEmbedded';
import Chip from 'components/Chip';

enum PostUnrealisedFXGainsTab {
  PayablesOrReceivables = 'payablesOrReceivables',
  Cash = 'cash',
}

const tabsData = {
  [PostUnrealisedFXGainsTab.PayablesOrReceivables]: {
    title: 'Payables or receivables',
  },
  [PostUnrealisedFXGainsTab.Cash]: {
    title: 'Cash',
  },
};

const TabLabelWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

type Props = {
  onExit: () => void;
};

const PostUnrealisedFXGains: FC<Props> = ({ onExit }) => {
  const { selectedVehicleId } = useLayoutContext();
  const { vehicle } = useVehicle(selectedVehicleId);
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const periodStartParam = searchParams.get('periodStart');
  const periodEndParam = searchParams.get('periodEnd');
  const { periodId } = useParams<{ periodId?: string }>();
  const periodStart = periodStartParam
    ? new CalendarDay(periodStartParam)
    : null;
  const periodEnd = periodEndParam ? new CalendarDay(periodEndParam) : null;
  const history = useHistory();

  const [currencyRates, setCurrencyRates] = useState<
    Array<[CurrencyCode, CurrencyRateDetails]>
  >([]);
  const [currencyRateOverrides, setCurrencyRateOverrides] = useState<
    Map<CurrencyCode, CurrencyRateDetails>
  >(new Map());

  const payablesPagination = usePagination({
    id: 'post-unrealised-fx-gains',
    order: 'desc' as const,
    orderBy: 'transactionDate',
  });

  const overridesEntry =
    currencyRateOverrides.size > 0
      ? { currencyRateOverrides: Object.fromEntries(currencyRateOverrides) }
      : {};

  const queryParams = {
    vehicleId: vehicle?._id ?? '',
    periodStart: periodStart ?? new CalendarDay(new Date()),
    periodEnd: periodEnd ?? new CalendarDay(new Date()),
  };

  const {
    data: payablesData,
    isFetching: payablesIsFetching,
    refetch: payablesRefetch,
  } = api.unrealisedFxGainOrLoss.list.useQuery({
    ...queryParams,
    page: payablesPagination.page,
    rowsPerPage: payablesPagination.rowsPerPage,
    order: payablesPagination.order,
    orderBy: payablesPagination.orderBy,
    ...overridesEntry,
  });

  const {
    data: cashData,
    isFetching: cashIsFetching,
    refetch: cashRefetch,
  } = api.unrealisedCashFxGainOrLoss.list.useQuery({
    ...queryParams,
    ...overridesEntry,
  });

  useEffect(() => {
    void payablesRefetch();
    void cashRefetch();
  }, [currencyRateOverrides, payablesRefetch, cashRefetch]);

  const selectedRecordId = searchParams.get('selectedRecordId');
  const setSelectedRecordId = (id: string | null) => {
    const params = new URLSearchParams(search);
    if (id) {
      params.set('selectedRecordId', id);
    } else {
      params.delete('selectedRecordId');
    }
    history.push({ search: params.toString() });
  };

  const selectedCashRecordId = searchParams.get('selectedCashRecordId');
  const setSelectedCashRecordId = (id: string | null) => {
    const params = new URLSearchParams(search);
    if (id) {
      params.set('selectedCashRecordId', id);
    } else {
      params.delete('selectedCashRecordId');
    }
    history.push({ search: params.toString() });
  };

  const [isProcessingApproval, setIsProcessingApproval] = useState(false);
  const [isPending, setIsPending] = useState(false);

  const approveRef = useRef<(() => Promise<void>) | null>(null);

  const { hasEqualisationInDraftState } = useEqualisations(
    selectedVehicleId ?? '',
  );
  const confirmDialog = useDisclosure();

  const handleMutation = useHandleTrpcMutation<string>();
  const { mutateAsync: confirmChecklistItems } =
    api.accountingPeriod.confirmChecklistItems.useMutation(
      handleMutation({ refetchType: 'active' }),
    );

  const handleApproveSuccess = async () => {
    try {
      if (!selectedVehicleId || !periodId) {
        throw new Error('Invalid vehicle or period ID');
      }
      await confirmChecklistItems({
        vehicleId: selectedVehicleId,
        id: periodId,
        items: ['unrealisedFxGainsAndLosses'],
        confirmed: true,
      });
      history.push(createLpPeriodEndReviewDetailRoute(periodId));
    } catch {
      setIsProcessingApproval(false);
    }
  };

  const handleApproveClick = () => {
    if (hasEqualisationInDraftState) {
      confirmDialog.onOpen();
    } else {
      void handleApprove();
    }
  };

  const handleCurrenciesLoaded = (
    currencies: Array<[CurrencyCode, CurrencyRateDetails]>,
  ) => {
    setCurrencyRates(currencies);
  };

  const handleApprove = async () => {
    setIsPending(true);
    setIsProcessingApproval(true);
    try {
      await approveRef.current?.();
    } catch {
      setIsProcessingApproval(false);
    } finally {
      setIsPending(false);
    }
  };

  if (!vehicle) {
    return null;
  }

  const handleExit = () => {
    onExit();
  };

  const payablesCount = payablesData?.transactions.totalCount ?? 0;
  const cashCount = cashData?.results?.length ?? 0;

  const tabCounts = {
    [PostUnrealisedFXGainsTab.PayablesOrReceivables]: payablesCount,
    [PostUnrealisedFXGainsTab.Cash]: cashCount,
  };

  const currencyRateJsx =
    currencyRates.length > 0 ? (
      <Box>
        <Typography variant="h6" color="text.secondary">
          {pluralize('Transaction currency rate', currencyRates.length ?? 0)}:
        </Typography>
        {currencyRates.map(([currencyCode, fxRate], index) => (
          <CurrencyRate
            key={`${currencyCode}-${index}`}
            fxRate={fxRate}
            currency={currencyCode}
            vehicle={vehicle}
            isFetchingRate={false}
            onRateChange={(rateDetails) => {
              setCurrencyRateOverrides((prev) => {
                const newMap = new Map(prev);
                newMap.set(currencyCode, rateDetails);
                return newMap;
              });
            }}
          />
        ))}
      </Box>
    ) : null;

  return (
    <FullPageDrawer
      title={
        selectedRecordId
          ? 'Unrealised FX Gain/Loss Details'
          : selectedCashRecordId
          ? 'Unrealised Cash FX Gain/Loss Details'
          : 'Post unrealised FX gains and losses'
      }
      open
      innerScroll
      size="lg"
      onClose={handleExit}
      actions={
        <Row fullWidth justify="space-between">
          <Button
            size="large"
            variant="outlined"
            onClick={
              selectedRecordId
                ? () => setSelectedRecordId(null)
                : selectedCashRecordId
                ? () => setSelectedCashRecordId(null)
                : handleExit
            }
          >
            {selectedRecordId || selectedCashRecordId ? 'Back' : 'Exit'}
          </Button>
          {!selectedRecordId && !selectedCashRecordId && (
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleApproveClick}
              disabled={isPending}
            >
              Approve and mark as reviewed
            </Button>
          )}
        </Row>
      }
    >
      <Column spacing="md" fullWidth>
        {selectedRecordId ? (
          <PayablesOrReceivablesHistoryDetail recordId={selectedRecordId} />
        ) : selectedCashRecordId ? (
          <CashHistoryDetailEmbedded recordId={selectedCashRecordId} />
        ) : isProcessingApproval ? (
          <ProcessingPage
            title="Approving transactions"
            subtitle="This will only take a moment."
          />
        ) : (
          <TabsPanel
            keepAlive
            tabs={Object.entries(tabsData).map(([tab, { title }]) => ({
              key: tab as PostUnrealisedFXGainsTab,
              label: (
                <TabLabelWrapper>
                  {title}
                  <Chip variant="tabIndicator" small bold={false}>
                    {tabCounts[tab as PostUnrealisedFXGainsTab]}
                  </Chip>
                </TabLabelWrapper>
              ),
              content:
                tab === PostUnrealisedFXGainsTab.PayablesOrReceivables ? (
                  <>
                    {currencyRateJsx}
                    <PayablesOrReceivables
                      vehicleId={vehicle._id}
                      periodEnd={periodEnd ?? new CalendarDay(new Date())}
                      currencyRateOverrides={currencyRateOverrides}
                      data={payablesData}
                      isFetching={payablesIsFetching}
                      pagination={payablesPagination}
                      onCurrenciesLoaded={handleCurrenciesLoaded}
                      onApproveSuccess={handleApproveSuccess}
                      approveRef={approveRef}
                      onViewRecord={setSelectedRecordId}
                    />
                  </>
                ) : (
                  <>
                    {currencyRateJsx}
                    <Cash
                      vehicleId={vehicle._id}
                      currencyRateOverrides={currencyRateOverrides}
                      data={cashData}
                      isFetching={cashIsFetching}
                      onCurrenciesLoaded={handleCurrenciesLoaded}
                      onApproveSuccess={handleApproveSuccess}
                      approveRef={approveRef}
                      onViewRecord={setSelectedCashRecordId}
                    />
                  </>
                ),
            }))}
            defaultTab={PostUnrealisedFXGainsTab.PayablesOrReceivables}
          />
        )}
      </Column>
      {hasEqualisationInDraftState && (
        <PendingDraftEqualisationsModal
          isOpen={confirmDialog.isOpen}
          onClose={confirmDialog.onClose}
          onConfirm={() => void handleApprove()}
          loading={isPending}
        />
      )}
    </FullPageDrawer>
  );
};

export default PostUnrealisedFXGains;
